/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Ingredient, PreparationStep } from '../admin/RecipeApi';

/**
 * Transforms ingredients from API response to ensure name and contraindications are always present
 * Handles both 'name'/'ingredient_name' and 'contraindications'/'contra_indications' fields
 * Also flattens nested RecipeIngredient / recipe_ingredient through-table attributes
 */
export const transformIngredients = (
  ingredients: Ingredient[] | undefined | null,
): Ingredient[] => {
  if (!ingredients || !Array.isArray(ingredients)) {
    return [];
  }

  return ingredients.map((ing: Ingredient) => {
    const ingAny = ing as unknown as Record<string, unknown>;
    const ri = (ingAny.RecipeIngredient ||
      ingAny.recipe_ingredient ||
      {}) as Record<string, unknown>;
    const contraRaw =
      ing.contraindications ??
      ingAny.contra_indications ??
      ri.contra_indications ??
      ri.contraindications;
    const contraStr = Array.isArray(contraRaw)
      ? contraRaw
          .map((v) => String(v ?? '').trim())
          .filter(Boolean)
          .join(', ')
      : typeof contraRaw === 'string'
        ? contraRaw
        : contraRaw != null
          ? String(contraRaw)
          : '';
    const riId = ri.id ?? ingAny.id;
    return {
      ...ing,
      ...ri,
      // Prefer join-table id so substitute parent links resolve
      id:
        (ri.id as string | number | undefined) ??
        (riId as string | number | undefined) ??
        ing.id,
      ingredient_id:
        (ingAny.ingredient_id as string | number | undefined) ??
        (ri.ingredient_id as string | number | undefined) ??
        (ri.id != null && ingAny.id !== ri.id
          ? (ingAny.id as string | number | undefined)
          : undefined),
      name:
        ing.name || (ing as { ingredient_name?: string }).ingredient_name || '',
      quantity:
        String(ri.quantity ?? ing.quantity ?? '').trim() || ing.quantity,
      unit: (ri.unit as string | undefined) ?? ing.unit,
      qualification:
        (ri.qualification as string | undefined) ??
        (ing as { qualification?: string }).qualification,
      is_substitute: !!(ri.is_substitute ?? ingAny.is_substitute),
      skip_for_contraindication: !!(
        ri.skip_for_contraindication ?? ingAny.skip_for_contraindication
      ),
      parent_recipe_ingredient_id:
        (ri.parent_recipe_ingredient_id as
          | string
          | number
          | null
          | undefined) ??
        (ingAny.parent_recipe_ingredient_id as
          | string
          | number
          | null
          | undefined),
      type: ((ri.type as string | undefined) ??
        (ingAny.type as string | undefined) ??
        'ingredient') as 'ingredient' | 'medicine',
      // Medicine dosage fields (join-table names + common aliases)
      how_often: ri.how_often ?? ingAny.how_often ?? ingAny.frequency_id,
      timing: ri.timing ?? ingAny.timing ?? ingAny.timing_id,
      meal_timing:
        ri.meal_timing ?? ingAny.meal_timing ?? ingAny.meal_timing_id,
      duration_value:
        ri.duration_value ?? ingAny.duration_value ?? ingAny.duration_days,
      duration_unit:
        ri.duration_unit ?? ingAny.duration_unit ?? ingAny.duration_unit_id,
      can_use_indefinitely: !!(
        ri.can_use_indefinitely ??
        ingAny.can_use_indefinitely ??
        ingAny.is_indefinite
      ),
      stop_after: ri.stop_after ?? ingAny.stop_after ?? ingAny.max_limit_days,
      stop_after_unit:
        ri.stop_after_unit ??
        ingAny.stop_after_unit ??
        ingAny.max_duration_unit_id,
      dosage_instructions: ri.dosage_instructions ?? ingAny.dosage_instructions,
      apply_topically: !!(ri.apply_topically ?? ingAny.apply_topically),
      apply_instruction: ri.apply_instruction ?? ingAny.apply_instruction,
      apply_for_value: ri.apply_for_value ?? ingAny.apply_for_value,
      apply_for_uom: ri.apply_for_uom ?? ingAny.apply_for_uom,
      contraindications: contraStr,
      contra_indications: contraStr,
    } as Ingredient & { type?: 'ingredient' | 'medicine' };
  });
};

/**
 * Prefer preparation-scoped ingredients (includes medicines with type) when present.
 */
export const collectRecipeIngredients = (
  recipe:
    | {
        ingredients?: Ingredient[] | null;
        preparations?: Array<{ ingredients?: Ingredient[] | null }> | null;
        medicines?: Ingredient[] | null;
      }
    | null
    | undefined,
  fallbackIngredients?: Ingredient[] | null,
): Ingredient[] => {
  const fromPreps = Array.isArray(recipe?.preparations)
    ? recipe!.preparations!.flatMap((p) =>
        Array.isArray(p?.ingredients) ? p.ingredients : [],
      )
    : [];
  const root =
    (Array.isArray(fallbackIngredients) && fallbackIngredients.length > 0
      ? fallbackIngredients
      : null) ||
    (Array.isArray(recipe?.ingredients) ? recipe!.ingredients! : []);
  const source = fromPreps.length > 0 ? fromPreps : root;
  const transformed = transformIngredients(source);

  // If medicines were only returned at recipe.medicines, merge any missing ones
  const topMeds = Array.isArray(recipe?.medicines) ? recipe!.medicines! : [];
  if (topMeds.length === 0) return transformed;

  const existingKeys = new Set(
    transformed.map((i) =>
      String(
        (i as { id?: unknown }).id ??
          (i as { ingredient_id?: unknown }).ingredient_id ??
          i.name ??
          '',
      ),
    ),
  );
  const extra = transformIngredients(topMeds).filter((m) => {
    const key = String(
      (m as { id?: unknown }).id ??
        (m as { ingredient_id?: unknown }).ingredient_id ??
        m.name ??
        '',
    );
    return key && !existingKeys.has(key);
  });
  return extra.length > 0 ? [...transformed, ...extra] : transformed;
};

/**
 * Collect preparation steps from ALL preparations (not only the first).
 */
export const collectRecipePreparationSteps = (
  recipe:
    | {
        steps?: PreparationStep[] | null;
        preparationSteps?: PreparationStep[] | null;
        preparation_steps?: PreparationStep[] | null;
        preparations?: Array<{
          steps?: PreparationStep[] | null;
          preparation_steps?: PreparationStep[] | null;
        }> | null;
      }
    | null
    | undefined,
  fallbackSteps?: PreparationStep[] | null,
): PreparationStep[] => {
  const fromPreps = Array.isArray(recipe?.preparations)
    ? recipe!.preparations!.flatMap((p) => {
        const list = p?.preparation_steps || p?.steps;
        return Array.isArray(list) ? list : [];
      })
    : [];
  const root =
    (Array.isArray(fallbackSteps) && fallbackSteps.length > 0
      ? fallbackSteps
      : null) ||
    (Array.isArray(recipe?.preparationSteps)
      ? recipe!.preparationSteps!
      : null) ||
    (Array.isArray(recipe?.preparation_steps)
      ? recipe!.preparation_steps!
      : null) ||
    (Array.isArray(recipe?.steps) ? recipe!.steps! : []);
  const source = fromPreps.length > 0 ? fromPreps : root;
  return transformPreparationSteps(source);
};

/** Resolve display text for an ingredient's contraindications from any known shape */
export const getIngredientContraindicationText = (
  ing: Record<string, unknown> | null | undefined,
): string => {
  if (!ing) return '';
  const ri = (ing.RecipeIngredient || ing.recipe_ingredient || {}) as Record<
    string,
    unknown
  >;
  const raw =
    ing.contraindications ??
    ing.contra_indications ??
    ri.contra_indications ??
    ri.contraindications;
  if (Array.isArray(raw)) {
    return raw
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }
  return typeof raw === 'string'
    ? raw.trim()
    : raw != null
      ? String(raw).trim()
      : '';
};

/**
 * Transforms preparation steps from API response to ensure compatibility
 * Handles both 'title'/'description' and 'instruction' fields
 * Handles both 'step' and 'step_number' fields
 */
// Helper to clean local URLs
const cleanUrl = (url?: unknown): string | undefined => {
  if (!url || typeof url !== 'string') {
    // If it's not a string (e.g. File object), return undefined or stringify if needed.
    // For our purposes, we only want to clean strings. If it's a File object, we can return it as any if typescript complains, but the signature returns string | undefined.
    // Wait, the return type is string | undefined. If we return the file object, it'll fail type check unless we cast. Let's just return url as any.
    return url as any;
  }
  if (typeof url === 'string' && url.includes('/uploads/')) {
    const index = url.indexOf('/uploads/');
    return url.substring(index);
  }
  return url as string;
};

export const transformPreparationSteps = (
  steps: PreparationStep[] | undefined | null,
): PreparationStep[] => {
  if (!steps || !Array.isArray(steps)) {
    return [];
  }

  return steps.map((step: PreparationStep) => ({
    ...step,
    title: step.title || step.instruction || '',
    description: step.description || step.instruction || '',
    step: step.step || step.step_number,
    // Clean URLs to remove domain (e.g., ngrok) if it's a local upload
    image: cleanUrl(step.image || step.image_url),
    image_url: cleanUrl(step.image || step.image_url),
    video: cleanUrl(step.video || step.video_url),
    video_url: cleanUrl(step.video || step.video_url),
  }));
};
